import { SmallAlertBox, useToast } from '@entur/alert'
import { SecondaryButton, SecondarySquareButton } from '@entur/button'
import { GridContainer, GridItem } from '@entur/grid'
import { AddIcon, CheckIcon, CloseIcon } from '@entur/icons'
import { colors } from '@entur/tokens'
import { Heading5, SmallText } from '@entur/typography'
import React, { useState, useEffect } from 'react'
import { Employee, FetchState } from '../../api-clients/api-types'
import { useApi } from '../../api-clients/client-hooks'
import { getEmployee } from '../../api-clients/eba-clients'
import { useEmployee } from '../../contexts/employee-context'
import { useCategory } from '../../hooks/employee-yearly-data-hooks'
import { useMultipleStatuses } from '../../hooks/multiple-statuses'
import { useCreateManualOrder } from '../../hooks/use-manual-order'
import { useGenerateTicketRights } from '../../hooks/use-ticket-rights'
import { validateFromDateToDate, validateStationPairs } from '../../utils/ticket-rights-utils'
import { getEndOfYear, tolLocalDateFormat, toPrintFormat } from '../../utils/date-utils'
import { capitalize } from '../../utils/string-utils'
import TicketTypeCategoryDropdown, {
  getSelectedUpdateTypeByValue,
  ticketTypeCategoryFormField,
  UpdateTypes,
} from '../form/ticket-type-category-dropdown'
import ValidatableDatePicker, { datePickerFormField } from '../form/validatable-date-picker'
import { useStationPairs } from '../../hooks/use-station-pairs'

type NewTicketRightProps = {
  employee: Employee
  setOpen: (open: boolean) => void
}
export const NewTicketRight: React.FC<NewTicketRightProps> = (props) => {
  const [selected, setSelected] = useState(ticketTypeCategoryFormField())
  const updateType = getSelectedUpdateTypeByValue(selected.value)
  const { addToast } = useToast()
  const { employee, setTicketRights } = useEmployee()
  const [employeeResponse, fetchEmployee] = useApi(getEmployee, {
    successCallback: (e) => {
      setTicketRights(e.ticketRights ?? [])
      props.setOpen(false)
    },
  })

  // Manual order
  const [fromDate, setFromDate] = useState(datePickerFormField('fromDate', 'Gyldig fra', new Date(), true))
  const [toDate, setToDate] = useState(datePickerFormField('toDate', 'Gyldig til', getEndOfYear(), true))
  const { stationPairs, addStationPairs } = useStationPairs()

  const [setManualOrderResponse, setManualOrder] = useCreateManualOrder((success) => {
    addToast({
      title: 'Rettighet lagt til!',
      content: `${capitalize(success.ticketType.description)} registrert.`,
      variant: 'success',
    })

    fetchEmployee({
      id: employee.id,
    })
  })

  // Categories
  const currentYear = new Date().getFullYear()
  const nextYear = currentYear + 1
  const [generateTicketRightsResponse, generateTicketRights] = useGenerateTicketRights(() => props.setOpen(false))
  const [setCategoryCurrentYearResponse, setCategoryCurrentYear] = useCategory(currentYear)
  const [setCategoryNextYearResponse, setCategoryNextYear] = useCategory(nextYear)
  const allCategoriesRequests = useMultipleStatuses([
    { status: setCategoryCurrentYearResponse.status, fieldNames: [`Kategori for ${currentYear}`] },
    { status: setCategoryNextYearResponse.status, fieldNames: [`Kategori for ${nextYear}`] },
  ])

  const registerNewTicketRight = () => {
    const fromDateToDateErrorMessage =
      updateType.type === UpdateTypes.TICKET_TYPE_PATH || updateType.type === UpdateTypes.TICKET_TYPE_APPRENTICE
        ? validateFromDateToDate(fromDate.value, toDate.value)
        : undefined
    const stationPairsErrorMessage = validateStationPairs(stationPairs)
    setErrorMsg(fromDateToDateErrorMessage ?? stationPairsErrorMessage)

    if (updateType.type === UpdateTypes.TICKET_TYPE_APPRENTICE && !fromDateToDateErrorMessage) {
      setManualOrder(
        parseInt(updateType.value, 10),
        tolLocalDateFormat(fromDate.value || new Date()),
        tolLocalDateFormat(toDate.value || new Date())
      )
    }

    if (
      updateType.type === UpdateTypes.TICKET_TYPE_PATH &&
      !fromDateToDateErrorMessage &&
      !stationPairsErrorMessage
    ) {
      setManualOrder(
        parseInt(updateType.value, 10),
        tolLocalDateFormat(fromDate.value || new Date()),
        tolLocalDateFormat(toDate.value || new Date()),
        stationPairs
          .filter((pair) => pair.fromStation && pair.toStation)
          .map((pair) => {
            return {
              fromStation: pair.fromStation || '',
              toStation: pair.toStation || '',
            }
          })
      )
    }

    if (updateType.type === UpdateTypes.CATEGORY) {
      setCategoryCurrentYear(updateType.value)
      setCategoryNextYear(updateType.value)
    }
  }

  const isLoading =
    allCategoriesRequests.status.status === FetchState.LOADING ||
    generateTicketRightsResponse.status === FetchState.LOADING ||
    setManualOrderResponse.status === FetchState.LOADING ||
    employeeResponse.status === FetchState.LOADING
  const [errorMsg, setErrorMsg] = useState<string>()

  useEffect(() => {
    if (allCategoriesRequests.status.status === FetchState.SUCCESS && !allCategoriesRequests.fetchFinished) {
      allCategoriesRequests.setFetchFinished(true)
      generateTicketRights()
    } else if (allCategoriesRequests.status.status === FetchState.LOADING && allCategoriesRequests.fetchFinished) {
      allCategoriesRequests.setFetchFinished(false)
    } else if (
      allCategoriesRequests.status.status === FetchState.ERROR ||
      setManualOrderResponse.status === FetchState.ERROR ||
      employeeResponse.status === FetchState.ERROR
    ) {
      setErrorMsg('En teknisk feil har oppstått')
    }
  }, [generateTicketRights, allCategoriesRequests, setManualOrderResponse, employeeResponse, setErrorMsg])

  return (
    <form
      className="new-ticket-right-card"
      data-cy="new-ticket-right-card"
      onSubmit={(e) => {
        registerNewTicketRight()
        e.preventDefault()
      }}
    >
      <GridContainer spacing="small" rowSpacing="medium">
        <GridItem small={6}>
          <Heading5 style={{ margin: '0' }}>Ny rettighet</Heading5>
        </GridItem>
        <GridItem small={6} style={{ textAlign: 'right' }}>
          <SmallText style={{ display: 'block' }}>Opprettet: {toPrintFormat(new Date())}</SmallText>
        </GridItem>

        <GridItem small={12}>
          <TicketTypeCategoryDropdown formField={selected} company={props.employee.company} update={setSelected} />
        </GridItem>

        {(updateType.type === UpdateTypes.TICKET_TYPE_APPRENTICE ||
          updateType.type === UpdateTypes.TICKET_TYPE_PATH) && (
          <>
            <GridItem small={6}>
              <ValidatableDatePicker date={fromDate} update={(date) => setFromDate(date)} />
            </GridItem>
            <GridItem small={6}>
              <ValidatableDatePicker date={toDate} update={(date) => setToDate(date)} />
            </GridItem>
            {updateType.type === UpdateTypes.TICKET_TYPE_PATH && stationPairs.map((pair) => pair.element)}
            {updateType.type === UpdateTypes.TICKET_TYPE_PATH && (
              <GridItem small={12}>
                <SecondarySquareButton type="button" onClick={addStationPairs}>
                  <AddIcon /> Legg til strekning
                </SecondarySquareButton>
              </GridItem>
            )}
          </>
        )}

        {errorMsg && (
          <GridItem small={12}>
            <SmallAlertBox variant="error">{errorMsg}</SmallAlertBox>
          </GridItem>
        )}

        <GridItem small={11}>
          <SecondaryButton loading={isLoading} style={{ width: '100%' }}>
            <CheckIcon color={colors.validation.mintContrast} />
            <SmallText>Bestill rettighet</SmallText>
          </SecondaryButton>
        </GridItem>

        <GridItem small={1}>
          <SecondarySquareButton onClick={() => props.setOpen(false)}>
            <CloseIcon color={colors.validation.lavaContrast} />
          </SecondarySquareButton>
        </GridItem>
      </GridContainer>
    </form>
  )
}
